import React, { useState } from 'react';
import axios from 'axios';
import { X, Lock, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import PasswordInput from './PasswordInput';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

const ChangePasswordModal = ({ open, onClose }) => {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const reset = () => {
    setCurrent(''); setNext(''); setConfirm('');
    setError(''); setSuccess(false); setSubmitting(false);
  };

  const close = () => { reset(); onClose(); };

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (next.length < 6) { setError('New password must be at least 6 characters'); return; }
    if (next !== confirm) { setError('Passwords do not match'); return; }
    if (next === current) { setError('New password must be different from the current one'); return; }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: current, new_password: next },
        { withCredentials: true }
      );
      setSuccess(true);
      toast.success('Password updated successfully');
      setTimeout(close, 1200);
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={close}
      data-testid="change-password-modal"
    >
      <div
        className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={close}
          className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-muted text-muted-foreground"
          aria-label="Close"
          data-testid="close-change-password-btn"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Lock className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-bold font-heading tracking-tight">Change password</h2>
            <p className="text-xs text-muted-foreground">Choose a strong password you don&apos;t use elsewhere</p>
          </div>
        </div>

        {success ? (
          <div className="flex flex-col items-center text-center py-6" data-testid="change-password-success">
            <CheckCircle2 className="w-10 h-10 text-green-500 mb-2" />
            <p className="text-sm text-foreground">Password updated successfully</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3" data-testid="change-password-form">
            <PasswordInput
              required
              placeholder="Current password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              data-testid="input-current-password"
            />
            <PasswordInput
              required
              minLength={6}
              placeholder="New password (min 6 chars)"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
              data-testid="input-new-password"
            />
            <PasswordInput
              required
              minLength={6}
              placeholder="Confirm new password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              data-testid="input-confirm-password"
            />

            {error && (
              <p className="text-sm text-destructive text-center" data-testid="change-password-error">{error}</p>
            )}

            <div className="flex gap-2 pt-1">
              <Button type="button" variant="outline" onClick={close} className="flex-1 h-11" data-testid="cancel-change-password-btn">
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="flex-1 h-11" data-testid="submit-change-password-btn">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Update password
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ChangePasswordModal;
